import { useContext, useState } from "react";
import { ButtonDS, GlobalIcon, ModalDS } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";

/**
 * IT recesso (D.lgs. 209/2025): right-of-withdrawal request flow, available
 * during the first 30 days after contract signature. Ported from the legacy
 * NewPolicy page so the rebuilt MyPolicy keeps the legally-required withdrawal
 * path. The IT / 30-day gating lives in the parent (MyPolicy); this component
 * only renders the entry button + confirmation modal and performs the request.
 * The request is re-validated server-side.
 */
const WithdrawalNotice = () => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [withdrawalModal, setWithdrawalModal] = useState<boolean>(false);
  const [withdrawalLoading, setWithdrawalLoading] = useState<boolean>(false);
  const [withdrawalDone, setWithdrawalDone] = useState<boolean>(false);

  const submitWithdrawal = async () => {
    if (withdrawalLoading) return;
    setWithdrawalLoading(true);
    try {
      const leadRepository = new HttpLeadRepository(leadId, token);
      await leadRepository.postWithdrawalRequest();
      setWithdrawalDone(true);
    } catch (error) {
      setNotification({
        message: translate("newDashboard.policy.withdrawal.error"),
        type: "error",
        time: 5,
      });
    } finally {
      setWithdrawalLoading(false);
    }
  };

  const closeWithdrawalModal = () => {
    setWithdrawalModal(false);
    setWithdrawalDone(false);
  };

  return (
    <div style={{ textAlign: "center", paddingTop: 8 }}>
      <ButtonDS
        label={translate("newDashboard.policy.withdrawal.button")}
        buttonType="secondary"
        size="32"
        ghost={true}
        className="w-fit mx-auto text-[#7B7B7B] underline"
        onClick={() => setWithdrawalModal(true)}
      />
      <ModalDS
        onClose={closeWithdrawalModal}
        open={withdrawalModal}
        title={translate(
          withdrawalDone
            ? "newDashboard.policy.withdrawal.successTitle"
            : "newDashboard.policy.withdrawal.modalTitle"
        )}
        content={
          <div>
            <GlobalIcon
              iconName={withdrawalDone ? "CheckedIcon" : "ContractFailedIcon"}
              size="L"
            />
            <p
              className="mt-[16px] text-[var(--dark-gray)] BodyL"
              dangerouslySetInnerHTML={{
                __html: translate(
                  withdrawalDone
                    ? "newDashboard.policy.withdrawal.successDescription"
                    : "newDashboard.policy.withdrawal.modalDescription"
                ),
              }}
            />
          </div>
        }
        buttons={
          withdrawalDone ? (
            <div>
              <ButtonDS
                label={translate("newDashboard.policy.withdrawal.close")}
                className="min-w-[50%] ml-auto"
                onClick={closeWithdrawalModal}
              />
            </div>
          ) : (
            <div className="flex flex-row gap-[8px] justify-end">
              <ButtonDS
                label={translate("newDashboard.policy.withdrawal.cancel")}
                buttonType="secondary"
                ghost={true}
                disabled={withdrawalLoading}
                onClick={closeWithdrawalModal}
              />
              <ButtonDS
                label={translate("newDashboard.policy.withdrawal.confirm")}
                className="min-w-[50%]"
                disabled={withdrawalLoading}
                onClick={submitWithdrawal}
              />
            </div>
          )
        }
      />
    </div>
  );
};

export default WithdrawalNotice;
